import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Form, Button } from 'react-bootstrap';
import MainLayout from '../components/MainLayout';
import api from '../services/api';
import { API_CONFIG } from '../services/config';

const LivePage: React.FC = () => {
  const [formData, setFormData] = useState({
    min: '',
    max: '',
    plMin: '',
    plMax: '',
    tgt: '',
    tgtPercent: '',
    slRs: '',
    tDuration: '',
    tslX: '',
    tgtX: '',
    tslY: '',
    tgtY: '',
    xPercent: '5',
    yPercent: '3',
    x: '0',
    y: '0',
    xPerAvg: '0.00',
    yPerAvg: '0.00',
    nh: '0.00',
    nl: '0.00',
    nIntra: '0.00',
    ixp: '',
    ixl: '',
    nPercL: '',
    ivh: '',
    n15: '',
    nfut: '',
    ixpLow: '', ixpHigh: '',
    n15Low: '', n15High: '',
    ndLow: '', ndHigh: '',
    allPos: '',
    allNeg: '',
    n50Pos: '',
    n50Neg: '',
    allPosLow: '', allPosHigh: '',
    allNegLow: '', allNegHigh: '',
    n50PosLow: '', n50PosHigh: '',
    n50NegLow: '', n50NegHigh: '',
    nd: '',
    strikePositions: '',
    avePrice: '',
    qty: '',
    percent: '',
    rs: '',
    tgtXRs: '',
    tgtYRs: '',
    allPercL: '',
    n50PercL: '',
    n10PercL: '',
    n50P: '',
    allP: '',
    n50PLow: '', n50PHigh: '',
    allPLow: '', allPHigh: '',
    bnl: '',
    itl: '',
    t3l: '',
    t10p: '',
    tag: '',
    cr: '',
  });

  // NIX15 threshold — a per-NSE-exchange trade_settings column (nix15_value), moved here from
  // TradingAppBar.tsx along with its own live value + glow. Kept as separate state (not part of
  // formData) since it's a plain string input, not a live-polled display field.
  const [nix15Input, setNix15Input] = useState<string>('');

  const wsRef = useRef<WebSocket | null>(null);
  const [lastUpdate, setLastUpdate] = useState<string>('');
  const [wsConnected, setWsConnected] = useState<boolean>(true);
  const lastValuesRef = useRef<{[key: string]: string}>({});
  const lchRef = useRef<Record<string, { low: number | null; high: number | null }>>({});
  const lchSaveTimerRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  // Only update if value changed from last known value
  const updateFormDataSmooth = (newData: any) => {
    setFormData(prev => {
      const updated = { ...prev };
      let hasChanges = false;
      Object.keys(newData).forEach(key => {
        const newValue = newData[key];
        const lastValue = lastValuesRef.current[key];
        if (lastValue !== newValue) {
          updated[key as keyof typeof prev] = newValue;
          lastValuesRef.current[key] = newValue;
          hasChanges = true;
        }
      });
      return hasChanges ? updated : prev;
    });
  };

  // Save L/H to DB with debounce (avoid too many API calls)
  const saveLch = (fieldName: string, currentValue: number) => {
    const existing = lchRef.current[fieldName];
    const isNewLow  = existing?.low  === null || existing?.low  === undefined || currentValue < existing.low;
    const isNewHigh = existing?.high === null || existing?.high === undefined || currentValue > existing.high;
    if (!isNewLow && !isNewHigh) return; // no change needed

    // Update local ref immediately
    lchRef.current[fieldName] = {
      low:  isNewLow  ? currentValue : (existing?.low  ?? currentValue),
      high: isNewHigh ? currentValue : (existing?.high ?? currentValue)
    };

    // Debounce DB save — wait 500ms before sending
    if (lchSaveTimerRef.current[fieldName]) clearTimeout(lchSaveTimerRef.current[fieldName]);
    lchSaveTimerRef.current[fieldName] = setTimeout(async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_CONFIG.baseURL}/lch`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ field_name: fieldName, current_value: currentValue })
        });
        if (response.ok) {
          const data = await response.json();
          lchRef.current[fieldName] = { low: data.low, high: data.high };
          // Update formData with DB-confirmed values
          setFormData(prev => ({
            ...prev,
            [`${fieldName}Low`]:  data.low  !== null ? data.low.toFixed(2)  : prev[`${fieldName}Low`  as keyof typeof prev],
            [`${fieldName}High`]: data.high !== null ? data.high.toFixed(2) : prev[`${fieldName}High` as keyof typeof prev]
          }));
        }
      } catch (e) { /* silent */ }
    }, 500);
  };

  // Load today's L/H from DB on mount
  const loadLchFromDB = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_CONFIG.baseURL}/lch`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) return;
      const data = await response.json();
      lchRef.current = data;

      // Map DB fields to formData keys
      const fieldMap: Record<string, [string, string]> = {
        nd:     ['ndLow',     'ndHigh'],
        allP:   ['allPLow',   'allPHigh'],
        n50P:   ['n50PLow',   'n50PHigh'],
        ixp:    ['ixpLow',    'ixpHigh'],
        n15:    ['n15Low',    'n15High'],
        allPos: ['allPosLow', 'allPosHigh'],
        allNeg: ['allNegLow', 'allNegHigh'],
        n50Pos: ['n50PosLow', 'n50PosHigh'],
        n50Neg: ['n50NegLow', 'n50NegHigh'],
      };

      const updates: Record<string, string> = {};
      for (const [field, [lowKey, highKey]] of Object.entries(fieldMap)) {
        const entry = data[field];
        if (entry) {
          if (entry.low  !== null) updates[lowKey]  = parseFloat(entry.low).toFixed(2);
          if (entry.high !== null) updates[highKey] = parseFloat(entry.high).toFixed(2);
        }
      }
      if (Object.keys(updates).length > 0) {
        setFormData(prev => ({ ...prev, ...updates }));
      }
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    const fetchTradeSettings = async () => {
      try {
        const response = await api.get('/trade-settings');
        const xPercent = response.data.x_percentage?.toString() || '5';
        const yPercent = response.data.y_percentage?.toString() || '3';
        const tag = response.data.tag_value?.toString() || '';

        setFormData(prev => ({
          ...prev,
          xPercent,
          yPercent,
          tag
        }));
        setNix15Input(response.data.nix15_value != null ? parseFloat(response.data.nix15_value).toFixed(2) : '');

        // Initialize lastValuesRef with current percentages to prevent flickering
        lastValuesRef.current['xPercent'] = xPercent;
        lastValuesRef.current['yPercent'] = yPercent;
      } catch (error) {
        console.error('[LivePage] Error fetching trade settings:', error);
      }
    };

    fetchTradeSettings();
    loadLchFromDB();

    const fetchTargetConfigs = async () => {
      try {
        console.log('[LivePage] Fetching target configs...');
        const response = await api.get('/config/targets');
        console.log('[LivePage] Target configs response:', response.data);
        const { buyTargetByX, buyTargetByRs, sellTargetByY, sellTargetByRs } = response.data;
        console.log('[LivePage] Parsed values:', {
          tgtX: buyTargetByX?.target_x,
          tgtXRs: buyTargetByRs?.target_percentage,
          tgtY: sellTargetByY?.target_y,
          tgtYRs: sellTargetByRs?.target_percentage
        });
        updateFormDataSmooth({
          tgtX: buyTargetByX?.target_x?.toString() || '',
          tgtXRs: buyTargetByRs?.target_percentage?.toString() || '',
          tgtY: sellTargetByY?.target_y?.toString() || '',
          tgtYRs: sellTargetByRs?.target_percentage?.toString() || ''
        });
      } catch (error) {
        console.error('[LivePage] Error fetching target configs:', error);
      }
    };
    fetchTargetConfigs();
    const fetchLiveCounts = async () => {
      try {
        const response = await api.get('/live-data/counts');
        const data = response.data;

        setFormData(prev => ({
          ...prev,
          x: data.x_count?.toString() || '0',
          y: data.y_count?.toString() || '0',
          xPerAvg: (data.x_per_avg !== undefined && data.x_per_avg !== null) ? data.x_per_avg.toFixed(2) : '0.00',
          yPerAvg: (data.y_per_avg !== undefined && data.y_per_avg !== null) ? data.y_per_avg.toFixed(2) : '0.00',
          nh: (data.nh !== undefined && data.nh !== null) ? data.nh.toFixed(2) : '0.00',
          nl: (data.nl !== undefined && data.nl !== null) ? data.nl.toFixed(2) : '0.00',
          nIntra: (data.nh !== undefined && data.nh !== null && data.nl !== undefined && data.nl !== null)
            ? (data.nh + data.nl).toFixed(2) : '0.00',
          ixp: (data.ixp !== undefined && data.ixp !== null) ? data.ixp.toFixed(2) : '0.00',
          ixl: (data.ixl !== undefined && data.ixl !== null) ? data.ixl.toFixed(2) : '0.00',
          nPercL: (data.n_perc_l !== undefined && data.n_perc_l !== null) ? data.n_perc_l.toFixed(2) : '0.00',
          ivh: (data.ivh !== undefined && data.ivh !== null) ? data.ivh.toFixed(2) : '0.00',
          n15: (data.n15 !== undefined && data.n15 !== null) ? data.n15.toFixed(2) : '0.00',
          nfut: (data.nfut !== undefined && data.nfut !== null) ? data.nfut.toFixed(2) : '0.00',
          allPos: data.all_pos?.toString() || '0',
          allNeg: data.all_neg?.toString() || '0',
          n50Pos: data.n50_pos?.toString() || '0',
          n50Neg: data.n50_neg?.toString() || '0',
          nd: (data.nd !== undefined && data.nd !== null) ? data.nd.toFixed(2) : '0.00',
          allPercL: (data.all_perc_l !== undefined && data.all_perc_l !== null) ? data.all_perc_l.toFixed(2) : '0.00',
          n50PercL: (data.n50_perc_l !== undefined && data.n50_perc_l !== null) ? data.n50_perc_l.toFixed(2) : '0.00',
          n10PercL: (data.n10_perc_l !== undefined && data.n10_perc_l !== null) ? data.n10_perc_l.toFixed(2) : '0.00',
          n50P: (data.n50_p !== undefined && data.n50_p !== null) ? data.n50_p.toFixed(2) : '0.00',
          allP: (data.all_p !== undefined && data.all_p !== null) ? data.all_p.toFixed(2) : '0.00',
          bnl:  (data.bn   !== undefined && data.bn   !== null) ? data.bn.toFixed(2)   : '0.00',
          itl:  (data.it   !== undefined && data.it   !== null) ? data.it.toFixed(2)   : '0.00',
          t3l:  (data.t3   !== undefined && data.t3   !== null) ? data.t3.toFixed(2)   : '0.00',
          t10p: (data.t10t !== undefined && data.t10t !== null) ? data.t10t.toFixed(2) : '0.00',
          cr:   (data.cr   !== undefined && data.cr   !== null) ? data.cr.toFixed(2)   : '0.00',
          tgtX: (data.tgt_x !== undefined && data.tgt_x !== null && data.tgt_x > 0) ? data.tgt_x.toString() : prev.tgtX,
          tgtXRs: (data.tgt_x_rs !== undefined && data.tgt_x_rs !== null && data.tgt_x_rs > 0) ? data.tgt_x_rs.toString() : prev.tgtXRs,
          tgtY: (data.tgt_y !== undefined && data.tgt_y !== null && data.tgt_y > 0) ? data.tgt_y.toString() : prev.tgtY,
          tgtYRs: (data.tgt_y_rs !== undefined && data.tgt_y_rs !== null && data.tgt_y_rs > 0) ? data.tgt_y_rs.toString() : prev.tgtYRs
        }));
        setLastUpdate(new Date().toLocaleTimeString());
        setWsConnected(true);

        // Save L/H to DB
        if (data.nd       !== undefined && data.nd       !== null) saveLch('nd',     data.nd);
        if (data.all_p    !== undefined && data.all_p    !== null) saveLch('allP',   data.all_p);
        if (data.n50_p    !== undefined && data.n50_p    !== null) saveLch('n50P',   data.n50_p);
        if (data.ixp      !== undefined && data.ixp      !== null) saveLch('ixp',    data.ixp);
        if (data.n15      !== undefined && data.n15      !== null) saveLch('n15',    data.n15);
        if (data.all_pos  !== undefined && data.all_pos  !== null) saveLch('allPos', data.all_pos);
        if (data.all_neg  !== undefined && data.all_neg  !== null) saveLch('allNeg', data.all_neg);
        if (data.n50_pos  !== undefined && data.n50_pos  !== null) saveLch('n50Pos', data.n50_pos);
        if (data.n50_neg  !== undefined && data.n50_neg  !== null) saveLch('n50Neg', data.n50_neg);
      } catch (error: any) {
        console.error('[LivePage] Error fetching live counts:', error);
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          window.location.href = '/login';
        }
        setWsConnected(false);
      }
    };

    fetchLiveCounts();
    const interval = setInterval(fetchLiveCounts, 250);

    // Connect to WebSocket with retry logic
    const connectWebSocket = () => {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('[LivePage] No authentication token found');
        return null;
      }

      const wsUrl = `${API_CONFIG.wsURL}/ws/market-data`;
      console.log('[LivePage] Connecting to WebSocket:', wsUrl);

      try {
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          console.log('[LivePage] ✓ WebSocket connected successfully');
          setWsConnected(true);
          ws.send(JSON.stringify({ type: 'authenticate', token }));
        };

        ws.onerror = (error) => {
          console.error('[LivePage] ❌ WebSocket connection error:', error);
          console.log('[LivePage] Make sure backend server is running on port 5000');
        };

        ws.onclose = (event) => {
          console.log('[LivePage] WebSocket disconnected:', event.code, event.reason);
          setWsConnected(false);
          if (event.code !== 1000 && event.code !== 1001) {
            console.log('[LivePage] Attempting to reconnect in 3 seconds...');
            setTimeout(() => {
              if (wsRef.current === ws) {
                connectWebSocket();
              }
            }, 3000); // Increased from 50ms to 3000ms for proper reconnection delay
          }
        };

        return ws;
      } catch (error) {
        console.error('[LivePage] Failed to create WebSocket:', error);
        return null;
      }
    };

    const ws = connectWebSocket();

    if (ws) {
      ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        console.log('[LivePage] WebSocket message:', message);

        if (message.type === 'authenticated') {
          console.log('[LivePage] WebSocket authenticated:', message);
        }

        if (message.type === 'liveCounts') {
          console.log('[LivePage] Live counts received:', message.data);
          const { x_count, y_count, nh, nl, ixp, ixl, n_perc_l, ivh, n15, nfut, all_pos, all_neg, n50_pos, n50_neg } = message.data;

          updateFormDataSmooth({
            x: x_count?.toString() || '0',
            y: y_count?.toString() || '0',
            nh: (nh !== undefined && nh !== null) ? nh.toFixed(2) : '0.00',
            nl: (nl !== undefined && nl !== null) ? nl.toFixed(2) : '0.00',
            nIntra: (nh !== undefined && nh !== null && nl !== undefined && nl !== null)
              ? (nh + nl).toFixed(2) : '0.00',
            ixp: (ixp !== undefined && ixp !== null) ? ixp.toFixed(2) : '0.00',
            ixl: (ixl !== undefined && ixl !== null) ? ixl.toFixed(2) : '0.00',
            nPercL: (n_perc_l !== undefined && n_perc_l !== null) ? n_perc_l.toFixed(2) : '0.00',
            ivh: (ivh !== undefined && ivh !== null) ? ivh.toFixed(2) : '0.00',
            n15: (n15 !== undefined && n15 !== null) ? n15.toFixed(2) : '0.00',
            nfut: (nfut !== undefined && nfut !== null) ? nfut.toFixed(2) : '0.00',
            allPos: all_pos?.toString() || '0',
            allNeg: all_neg?.toString() || '0',
            n50Pos: n50_pos?.toString() || '0',
            n50Neg: n50_neg?.toString() || '0',
            nd: (message.data.nd !== undefined && message.data.nd !== null) ? message.data.nd.toFixed(2) : '0.00',
            allPercL: (message.data.all_perc_l !== undefined && message.data.all_perc_l !== null) ? message.data.all_perc_l.toFixed(2) : '0.00',
            n50PercL: (message.data.n50_perc_l !== undefined && message.data.n50_perc_l !== null) ? message.data.n50_perc_l.toFixed(2) : '0.00',
            n10PercL: (message.data.n10_perc_l !== undefined && message.data.n10_perc_l !== null) ? message.data.n10_perc_l.toFixed(2) : '0.00',
            n50P: (message.data.n50_p !== undefined && message.data.n50_p !== null) ? message.data.n50_p.toFixed(2) : '0.00',
            allP: (message.data.all_p !== undefined && message.data.all_p !== null) ? message.data.all_p.toFixed(2) : '0.00'
          });
          setFormData(prev => ({
            ...prev,
            allPosLow: prev.allPosLow === '' || parseInt(all_pos) < parseInt(prev.allPosLow) ? all_pos?.toString() || '0' : prev.allPosLow,
            allPosHigh: prev.allPosHigh === '' || parseInt(all_pos) > parseInt(prev.allPosHigh) ? all_pos?.toString() || '0' : prev.allPosHigh,
            allNegLow: prev.allNegLow === '' || parseInt(all_neg) < parseInt(prev.allNegLow) ? all_neg?.toString() || '0' : prev.allNegLow,
            allNegHigh: prev.allNegHigh === '' || parseInt(all_neg) > parseInt(prev.allNegHigh) ? all_neg?.toString() || '0' : prev.allNegHigh,
            n50PosLow: prev.n50PosLow === '' || parseInt(n50_pos) < parseInt(prev.n50PosLow) ? n50_pos?.toString() || '0' : prev.n50PosLow,
            n50PosHigh: prev.n50PosHigh === '' || parseInt(n50_pos) > parseInt(prev.n50PosHigh) ? n50_pos?.toString() || '0' : prev.n50PosHigh,
            n50NegLow: prev.n50NegLow === '' || parseInt(n50_neg) < parseInt(prev.n50NegLow) ? n50_neg?.toString() || '0' : prev.n50NegLow,
            n50PLow: prev.n50PLow === '' || parseFloat(message.data.n50_p) < parseFloat(prev.n50PLow) ? message.data.n50_p?.toFixed(2) || '0.00' : prev.n50PLow,
            n50PHigh: prev.n50PHigh === '' || parseFloat(message.data.n50_p) > parseFloat(prev.n50PHigh) ? message.data.n50_p?.toFixed(2) || '0.00' : prev.n50PHigh,
            allPLow: prev.allPLow === '' || parseFloat(message.data.all_p) < parseFloat(prev.allPLow) ? message.data.all_p?.toFixed(2) || '0.00' : prev.allPLow,
            allPHigh: prev.allPHigh === '' || parseFloat(message.data.all_p) > parseFloat(prev.allPHigh) ? message.data.all_p?.toFixed(2) || '0.00' : prev.allPHigh,
          }));
          setLastUpdate(new Date().toLocaleTimeString());
        }
      } catch (error) {
        console.error('[LivePage] WebSocket message error:', error);
      }
    };
    }
    return () => {
      clearInterval(interval);
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Moved here from TradingAppBar (which still reads tag_value for its own X/Y-count
  // highlight, just no longer shows an input for it) — same simple partial-update pattern as
  // handlePercentageBlur below.
  const handleTagBlur = async () => {
    try {
      await api.put('/trade-settings', { tag_value: formData.tag !== '' ? parseInt(formData.tag) : 0 });
      console.log(`[LivePage] Updated tag_value to ${formData.tag}`);
    } catch (error) {
      console.error('[LivePage] Error updating tag_value:', error);
    }
  };

  const handlePercentageBlur = async (field: 'xPercent' | 'yPercent') => {
    try {
      const dbField = field === 'xPercent' ? 'x_percentage' : 'y_percentage';
      await api.put('/trade-settings', { [dbField]: parseFloat(formData[field]) });
      console.log(`[LivePage] Updated ${dbField} to ${formData[field]}`);

      // Immediately fetch new X/Y counts after percentage change
      const response = await api.get('/live-data/counts');
      const data = response.data;

      // Update X and Y values immediately
      setFormData(prev => ({
        ...prev,
        x: data.x_count?.toString() || '0',
        y: data.y_count?.toString() || '0'
      }));

      // Update lastValuesRef to prevent flickering
      lastValuesRef.current['x'] = data.x_count?.toString() || '0';
      lastValuesRef.current['y'] = data.y_count?.toString() || '0';
    } catch (error) {
      console.error(`[LivePage] Error updating ${field}:`, error);
    }
  };

  return (
    <MainLayout>
      <Container className="py-1">
          <Card style={{ background: '#ffffff', border: '1px solid #dee2e6', borderRadius: '4px' }}>
            <Card.Header style={{ background: '#f8f9fa', borderBottom: '1px solid #dee2e6' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                {/* <h4 style={{ margin: 0, fontWeight: '600', fontSize: '18px' }}>LIVE PAGE</h4> */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <div style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: wsConnected ? '#4caf50' : '#f44336'
                    }}></div>
                    <small style={{ color: wsConnected ? '#4caf50' : '#f44336', fontWeight: '600' }}>
                      {wsConnected ? 'Connected' : 'Disconnected'}
                    </small>
                  </div>
                  <small style={{ color: '#6c757d' }}>Last Update: {lastUpdate}</small>
                </div>
              </div>
            </Card.Header>
            <Card.Body className="p-4" style={{ '--input-text-align': 'center' } as React.CSSProperties}>
              <style>{`
                .card-body input, .card-body select { text-align: center; }
              `}</style>
              {/* Row 1: TSL Y, X(% / Count), Y(% / Count), NH, NL, (empty), (empty), (empty)*/}

                <Row className="mb-3">
                {/* <Col>
                  <Form.Group>
                    <Form.Label style={{ fontSize: '13px', fontWeight: '600', display: 'block', textAlign: 'center' }}>TSL Y</Form.Label>
                    <Form.Control type="number" name="tslY" value={formData.tslY} onChange={handleChange} style={{ fontSize: '13px' }} />
                  </Form.Group>
                </Col> */}
                <Col xs={3}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: '12px', fontWeight: '600', display: 'block', textAlign: 'center' }}>X% / X / X%</Form.Label>
                    <div style={{ display: 'flex', gap: '3px' }}>
                      <Form.Control
                        type="number"
                        name="xPercent"
                        value={formData.xPercent}
                        onChange={handleChange}
                        onBlur={() => handlePercentageBlur('xPercent')}
                        style={{ fontSize: '13px', flex: 1, minWidth: 0, padding: '4px 2px' }}
                        step="0.1"
                      />
                      <Form.Control
                        type="text"
                        name="x"
                        value={formData.x}
                        readOnly
                        style={{ fontSize: '13px', background: '#f8f9fa', fontWeight: '600', flex: 1, minWidth: 0, padding: '4px 2px' }}
                      />
                      <Form.Control
                        type="text"
                        name="xPerAvg"
                        value={formData.xPerAvg}
                        readOnly
                        style={{ fontSize: '13px', background: '#e3f2fd', fontWeight: '600', flex: 1, minWidth: 0, padding: '4px 2px' }}
                      />
                    </div>
                  </Form.Group>
                </Col>
                <Col xs={3}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: '12px', fontWeight: '600', display: 'block', textAlign: 'center' }}>Y% / Y / Y%</Form.Label>
                    <div style={{ display: 'flex', gap: '3px' }}>
                      <Form.Control
                        type="number"
                        name="yPercent"
                        value={formData.yPercent}
                        onChange={handleChange}
                        onBlur={() => handlePercentageBlur('yPercent')}
                        style={{ fontSize: '13px', flex: 1, minWidth: 0, padding: '4px 2px' }}
                        step="0.1"
                      />
                      <Form.Control
                        type="text"
                        name="y"
                        value={formData.y}
                        readOnly
                        style={{ fontSize: '13px', background: '#f8f9fa', fontWeight: '600', flex: 1, minWidth: 0, padding: '4px 2px' }}
                      />
                      <Form.Control
                        type="text"
                        name="yPerAvg"
                        value={formData.yPerAvg}
                        readOnly
                        style={{ fontSize: '13px', background: '#fce4ec', fontWeight: '600', flex: 1, minWidth: 0, padding: '4px 2px' }}
                      />
                    </div>
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group>
                    <Form.Label style={{ fontSize: '13px', fontWeight: '600', display: 'block', textAlign: 'center' }}>IVH</Form.Label>
                    <Form.Control type="text" name="ivh" value={formData.ivh} readOnly style={{ fontSize: '13px', background: '#f8f9fa', fontWeight: '600' }} />
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group>
                    <Form.Label style={{ fontSize: '13px', fontWeight: '600', display: 'block', textAlign: 'center' }}>NL</Form.Label>
                    <Form.Control type="text" name="nl" value={formData.nl} readOnly style={{ fontSize: '13px', background: '#f8f9fa', fontWeight: '600' }} />
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group>
                    <Form.Label style={{ fontSize: '13px', fontWeight: '600', display: 'block', textAlign: 'center' }}>NH</Form.Label>
                    <Form.Control type="text" name="nh" value={formData.nh} readOnly style={{ fontSize: '13px', background: '#f8f9fa', fontWeight: '600' }} />
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group>
                    <Form.Label style={{ fontSize: '13px', fontWeight: '600', display: 'block', textAlign: 'center' }}>N-INTRA</Form.Label>
                    <Form.Control
                      type="text"
                      name="nIntra"
                      value={formData.nIntra}
                      readOnly
                      style={{
                        fontSize: '13px',
                        background: '#fff3e0',
                        fontWeight: '600',
                        border: '1px solid #ff9800',
                        color: '#e65100'
                      }}
                    />
                  </Form.Group>
                </Col>
                {/* <Col>
                  <Form.Group>
                    <Form.Label style={{ fontSize: '13px', fontWeight: '600', display: 'block', textAlign: 'center' }}>&nbsp;</Form.Label>
                    <Form.Control type="text" disabled style={{ fontSize: '13px', background: '#f8f9fa' }} />
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group>
                    <Form.Label style={{ fontSize: '13px', fontWeight: '600', display: 'block', textAlign: 'center' }}>&nbsp;</Form.Label>
                    <Form.Control type="text" disabled style={{ fontSize: '13px', background: '#f8f9fa' }} />
                  </Form.Group>
                </Col> */}
              </Row>

              {/* Row 2: Min, Max, TGT, TGT %, SL Rs, X, TGT X RS/X, TGT Y RS/Y   */}

              <Row className="mb-3">
                {/* P/L MIN, P/L LOW, P/L MAX, P/L HIGH — not needed (commented out per request) */}
                {/* <Col>
                  <Form.Group>
                    <div style={{ display: 'flex', gap: '4px', marginBottom: '2px' }}>
                      <small style={{ width: '50%', textAlign: 'center', fontSize: '10px', fontWeight: '600', color: '#495057' }}>P/L MIN</small>
                      <small style={{ width: '50%', textAlign: 'center', fontSize: '10px', fontWeight: '600', color: '#495057' }}>P/L LOW</small>
                    </div>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <Form.Control type="number" name="min" value={formData.min} onChange={handleChange} style={{ fontSize: '13px' }} />
                      <Form.Control type="number" name="plMin" value={formData.plMin} onChange={handleChange} style={{ fontSize: '13px', background: '#f8f9fa' }} />
                    </div>
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group>
                    <div style={{ display: 'flex', gap: '4px', marginBottom: '2px' }}>
                      <small style={{ width: '50%', textAlign: 'center', fontSize: '10px', fontWeight: '600', color: '#495057' }}>P/L MAX</small>
                      <small style={{ width: '50%', textAlign: 'center', fontSize: '10px', fontWeight: '600', color: '#495057' }}>P/L HIGH</small>
                    </div>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <Form.Control type="number" name="max" value={formData.max} onChange={handleChange} style={{ fontSize: '13px' }} />
                      <Form.Control type="number" name="plMax" value={formData.plMax} onChange={handleChange} style={{ fontSize: '13px', background: '#f8f9fa' }} />
                    </div>
                  </Form.Group>
                </Col> */}
                {/* <Col>
                  <Form.Group>
                    <Form.Label style={{ fontSize: '13px', fontWeight: '600', display: 'block', textAlign: 'center' }}>TGT</Form.Label>
                    <Form.Control type="number" name="tgt" value={formData.tgt} onChange={handleChange} style={{ fontSize: '13px' }} />
                  </Form.Group>
                </Col> */}
                {/* <Col>
                  <Form.Group>
                    <Form.Label style={{ fontSize: '13px', fontWeight: '600', display: 'block', textAlign: 'center' }}>TGT %</Form.Label>
                    <Form.Control type="number" name="tgtPercent" value={formData.tgtPercent} onChange={handleChange} style={{ fontSize: '13px' }} />
                  </Form.Group>
                </Col> */}
                {/* <Col>
                  <Form.Group>
                    <Form.Label style={{ fontSize: '13px', fontWeight: '600', display: 'block', textAlign: 'center' }}>SL Rs</Form.Label>
                    <Form.Control type="number" name="slRs" value={formData.slRs} onChange={handleChange} style={{ fontSize: '13px' }} />
                  </Form.Group>
                </Col> */}
                {/* <Col>
                  <Form.Group>
                    <Form.Label style={{ fontSize: '13px', fontWeight: '600', display: 'block', textAlign: 'center' }}>X</Form.Label>
                    <Form.Control type="number" name="tslX" value={formData.tslX} onChange={handleChange} style={{ fontSize: '13px' }} />
                  </Form.Group>
                </Col> */}
                <Col>
                  <Form.Group>
                    <Form.Label style={{ fontSize: '11px', fontWeight: '600', display: 'block', textAlign: 'center' }}>TGT X RS / X</Form.Label>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <Form.Control
                        type="text"
                        name="tgtXRs"
                        value={formData.tgtXRs}
                        readOnly
                        style={{ fontSize: '11px', background: '#e3f2fd', fontWeight: '600', width: '50%', padding: '4px' }}
                      />
                      <Form.Control
                        type="text"
                        name="tgtX"
                        value={formData.tgtX}
                        readOnly
                        style={{ fontSize: '11px', background: '#e3f2fd', fontWeight: '600', width: '50%', padding: '4px' }}
                      />
                    </div>
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group>
                    <Form.Label style={{ fontSize: '11px', fontWeight: '600', display: 'block', textAlign: 'center' }}>TGT Y RS / Y</Form.Label>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <Form.Control
                        type="text"
                        name="tgtYRs"
                        value={formData.tgtYRs}
                        readOnly
                        style={{ fontSize: '11px', background: '#fce4ec', fontWeight: '600', width: '50%', padding: '4px' }}
                      />
                      <Form.Control
                        type="text"
                        name="tgtY"
                        value={formData.tgtY}
                        readOnly
                        style={{ fontSize: '11px', background: '#fce4ec', fontWeight: '600', width: '50%', padding: '4px' }}
                      />
                    </div>
                  </Form.Group>
                </Col>
                <Col xs="auto">
                  <Form.Group>
                    <Form.Label style={{ fontSize: '11px', fontWeight: '600', display: 'block', textAlign: 'center' }}>Tag</Form.Label>
                    <Form.Control
                      type="number"
                      name="tag"
                      value={formData.tag}
                      onChange={handleChange}
                      onBlur={handleTagBlur}
                      maxLength={2}
                      onKeyDown={(e) => { if (['e', 'E', '+', '-', '.'].includes(e.key)) e.preventDefault(); }}
                      style={{ fontSize: '11px', fontWeight: '600', textAlign: 'center', padding: '4px', width: '60px' }}
                    />
                  </Form.Group>
                </Col>

                {/* NIX15, CR, IXL-N%L — moved here from TradingAppBar.tsx per client's explicit
                    ask, same formulas/colors as before, just relocated next to the TGT fields. */}
                <Col xs="auto">
                  {(() => {
                    const inputVal = parseFloat(nix15Input);
                    const hasInput = nix15Input !== '' && !isNaN(inputVal) && inputVal > 0;
                    const nix15Live = parseFloat(formData.n15) || 0;
                    const isGreen = hasInput && nix15Live >= inputVal;
                    const isRed = hasInput && nix15Live <= -inputVal;
                    return (
                      <Form.Group>
                        <Form.Label style={{ fontSize: '11px', fontWeight: '600', display: 'block', textAlign: 'center' }}>NIX15</Form.Label>
                        <div style={{
                          display: 'flex', borderRadius: '6px', transition: 'all 0.3s ease',
                          boxShadow: isGreen ? '0 0 12px 4px rgba(27, 94, 32, 0.7)' : isRed ? '0 0 12px 4px rgba(127, 0, 0, 0.7)' : 'none',
                        }}>
                          <input
                            type="number"
                            value={nix15Input}
                            onChange={(e) => setNix15Input(e.target.value)}
                            onBlur={() => api.put('/trade-settings', { nix15_value: nix15Input !== '' ? parseFloat(nix15Input) : null, exchange: 'NSE' }).catch(() => {})}
                            placeholder="0.00"
                            step="0.01"
                            style={{
                              width: '55px', padding: '4px', fontSize: '11px', textAlign: 'center',
                              border: '1px solid #d1d5db', borderRadius: '4px 0 0 4px', background: 'white', color: '#212529'
                            }}
                          />
                          <input
                            type="text" readOnly
                            value={hasInput ? nix15Live.toFixed(2) : '--'}
                            style={{
                              width: '55px', padding: '4px', fontSize: '11px', textAlign: 'center', fontWeight: '700',
                              border: isGreen ? '2px solid #1b5e20' : isRed ? '2px solid #7f0000' : '1px solid #d1d5db',
                              borderLeft: 'none', borderRadius: '0 4px 4px 0',
                              background: isGreen ? '#1b5e20' : isRed ? '#7f0000' : '#f8f9fa',
                              color: (isGreen || isRed) ? '#ffffff' : '#6c757d'
                            }}
                          />
                        </div>
                      </Form.Group>
                    );
                  })()}
                </Col>
                <Col xs="auto">
                  <Form.Group>
                    <Form.Label style={{ fontSize: '11px', fontWeight: '600', display: 'block', textAlign: 'center' }} title="Cumulative Recovery = Σ (Index Weight% × Individual Stock Recovery%)">CR</Form.Label>
                    <Form.Control
                      type="text" readOnly value={formData.cr || '0.00'}
                      style={{
                        fontSize: '11px', fontWeight: '700', textAlign: 'center', padding: '4px', width: '65px',
                        background: parseFloat(formData.cr || '0') >= 0 ? '#e8f5e9' : '#ffebee',
                        color: parseFloat(formData.cr || '0') >= 0 ? '#1b5e20' : '#7f0000',
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col xs="auto">
                  {(() => {
                    const ixlLive = parseFloat(formData.ixl) || 0;
                    const nPercLLive = parseFloat(formData.nPercL) || 0;
                    const diff = parseFloat((ixlLive - nPercLLive).toFixed(2));
                    const isPos = diff >= 0;
                    const hasData = ixlLive !== 0 || nPercLLive !== 0;
                    return (
                      <Form.Group>
                        <Form.Label style={{ fontSize: '11px', fontWeight: '600', display: 'block', textAlign: 'center' }}>IXL-N%L</Form.Label>
                        <Form.Control
                          type="text" readOnly value={hasData ? (isPos ? `+${diff}` : `${diff}`) : '--'}
                          style={{
                            fontSize: '11px', fontWeight: '700', textAlign: 'center', padding: '4px', width: '65px',
                            border: !hasData ? '1px solid #d1d5db' : isPos ? '2px solid #1b5e20' : '2px solid #7f0000',
                            background: !hasData ? '#f8f9fa' : isPos ? '#1b5e20' : '#7f0000',
                            color: !hasData ? '#6c757d' : '#ffffff'
                          }}
                        />
                      </Form.Group>
                    );
                  })()}
                </Col>
              </Row>

              {/* Row 3: IXP, IXL, N%L, IVH, N15, NFUT, No Of Stock (All), No Of Stock (N50) */}
              <Row className="mb-3" style={{ margin: 0 }}>
                {/* <Col>
                  <Form.Group>
                    <Form.Label style={{ fontSize: '13px', fontWeight: '600', display: 'block', textAlign: 'center' }}>IXP</Form.Label>
                    <Form.Control
                      type="text"
                      name="ixp"
                      value={formData.ixp}
                      readOnly
                      style={{
                        fontSize: '14px',
                        background: parseFloat(formData.ixp) >= 50 ? '#e8f5e9' : '#ffebee',
                        fontWeight: '700',
                        border: `2px solid ${parseFloat(formData.ixp) >= 50 ? '#4caf50' : '#f44336'}`,
                        borderRadius: '4px',
                        textAlign: 'center',
                        color: parseFloat(formData.ixp) >= 50 ? '#2e7d32' : '#c62828'
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group>
                    <Form.Label style={{ fontSize: '13px', fontWeight: '600', display: 'block', textAlign: 'center' }}>IXL</Form.Label>
                    <Form.Control
                      type="text"
                      name="ixl"
                      value={formData.ixl}
                      readOnly
                      style={{
                        fontSize: '14px',
                        background: parseFloat(formData.ixl) >= 50 ? '#e8f5e9' : '#ffebee',
                        fontWeight: '700',
                        border: `2px solid ${parseFloat(formData.ixl) >= 50 ? '#4caf50' : '#f44336'}`,
                        borderRadius: '4px',
                        textAlign: 'center',
                        color: parseFloat(formData.ixl) >= 50 ? '#2e7d32' : '#c62828'
                      }}
                    />
                  </Form.Group>
                </Col> */}

                {/* ND — not needed (commented out per request) */}
                {/* <Col xs={2} style={{ paddingLeft: '0px', paddingRight: '0px' }}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: '13px', fontWeight: '600', display: 'block', textAlign: 'center' }}>ND</Form.Label>
                    <div style={{ display: 'flex', gap: '2px', marginBottom: '2px' }}>
                      <small style={{ width: '33%', textAlign: 'center', fontSize: '9px', color: '#6c757d' }}>L</small>
                      <small style={{ width: '33%', textAlign: 'center', fontSize: '9px', color: '#6c757d' }}>C</small>
                      <small style={{ width: '33%', textAlign: 'center', fontSize: '9px', color: '#6c757d' }}>H</small>
                    </div>
                    <div style={{ display: 'flex', gap: '2px' }}>
                      <Form.Control type="text" readOnly value={formData.ndLow} style={{ fontSize: '11px', background: parseFloat(formData.ndLow) >= 0 ? '#c8e6c9' : '#ffcdd2', fontWeight: '700', border: `1px solid ${parseFloat(formData.ndLow) >= 0 ? '#1b5e20' : '#7f0000'}`, borderRadius: '4px', textAlign: 'center', color: parseFloat(formData.ndLow) >= 0 ? '#1b5e20' : '#7f0000', width: '33%', padding: '4px 2px' }} />
                      <Form.Control type="text" readOnly value={formData.nd} style={{ fontSize: '11px', background: parseFloat(formData.nd) >= 0 ? '#1b5e20' : '#7f0000', fontWeight: '700', border: `2px solid ${parseFloat(formData.nd) >= 0 ? '#1b5e20' : '#7f0000'}`, borderRadius: '4px', textAlign: 'center', color: '#ffffff', width: '33%', padding: '4px 2px' }} />
                      <Form.Control type="text" readOnly value={formData.ndHigh} style={{ fontSize: '11px', background: parseFloat(formData.ndHigh) >= 0 ? '#c8e6c9' : '#ffcdd2', fontWeight: '700', border: `1px solid ${parseFloat(formData.ndHigh) >= 0 ? '#1b5e20' : '#7f0000'}`, borderRadius: '4px', textAlign: 'center', color: parseFloat(formData.ndHigh) >= 0 ? '#1b5e20' : '#7f0000', width: '33%', padding: '4px 2px' }} />
                    </div>
                  </Form.Group>
                </Col> */}
                <Col xs={4} style={{ paddingLeft: '0px', paddingRight: '0px' }}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: '13px', fontWeight: '600', display: 'block', textAlign: 'center' }}>ALL P</Form.Label>
                    <div style={{ display: 'flex', gap: '2px', marginBottom: '2px' }}>
                      <small style={{ width: '33%', textAlign: 'center', fontSize: '9px', color: '#6c757d' }}>L</small>
                      <small style={{ width: '33%', textAlign: 'center', fontSize: '9px', color: '#6c757d' }}>C</small>
                      <small style={{ width: '33%', textAlign: 'center', fontSize: '9px', color: '#6c757d' }}>H</small>
                    </div>
                    <div style={{ display: 'flex', gap: '2px' }}>
                      <Form.Control type="text" readOnly value={formData.allPLow} style={{ fontSize: '11px', background: parseFloat(formData.allPLow) >= 0 ? '#c8e6c9' : '#ffcdd2', fontWeight: '700', border: `1px solid ${parseFloat(formData.allPLow) >= 0 ? '#1b5e20' : '#7f0000'}`, borderRadius: '4px', textAlign: 'center', color: parseFloat(formData.allPLow) >= 0 ? '#1b5e20' : '#7f0000', width: '33%', padding: '4px 2px' }} />
                      <Form.Control type="text" readOnly value={formData.allP} style={{ fontSize: '11px', background: parseFloat(formData.allP) >= 0 ? '#1b5e20' : '#7f0000', fontWeight: '700', border: `2px solid ${parseFloat(formData.allP) >= 0 ? '#1b5e20' : '#7f0000'}`, borderRadius: '4px', textAlign: 'center', color: '#ffffff', width: '33%', padding: '4px 2px' }} />
                      <Form.Control type="text" readOnly value={formData.allPHigh} style={{ fontSize: '11px', background: parseFloat(formData.allPHigh) >= 0 ? '#c8e6c9' : '#ffcdd2', fontWeight: '700', border: `1px solid ${parseFloat(formData.allPHigh) >= 0 ? '#1b5e20' : '#7f0000'}`, borderRadius: '4px', textAlign: 'center', color: parseFloat(formData.allPHigh) >= 0 ? '#1b5e20' : '#7f0000', width: '33%', padding: '4px 2px' }} />
                    </div>
                  </Form.Group>
                </Col>
                <Col xs={6} style={{ paddingLeft: '0px' }}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: '11px', fontWeight: '600', display: 'block', textAlign: 'center' }}>No Of Stock (All)</Form.Label>
                    <div style={{ display: 'flex', gap: '2px', marginBottom: '2px' }}>
                      <small style={{ width: '16.6%', textAlign: 'center', fontSize: '9px', color: '#6c757d' }}>L</small>
                      <small style={{ width: '16.6%', textAlign: 'center', fontSize: '9px', color: '#6c757d' }}>C</small>
                      <small style={{ width: '16.6%', textAlign: 'center', fontSize: '9px', color: '#6c757d' }}>H</small>
                      <small style={{ width: '16.6%', textAlign: 'center', fontSize: '9px', color: '#6c757d' }}>L</small>
                      <small style={{ width: '16.6%', textAlign: 'center', fontSize: '9px', color: '#6c757d' }}>C</small>
                      <small style={{ width: '16.6%', textAlign: 'center', fontSize: '9px', color: '#6c757d' }}>H</small>
                    </div>
                    <div style={{ display: 'flex', gap: '2px' }}>
                      <Form.Control type="text" readOnly value={formData.allPosLow} style={{ fontSize: '11px', background: '#c8e6c9', fontWeight: '700', border: '1px solid #1b5e20', borderRadius: '4px', textAlign: 'center', color: '#1b5e20', width: '16.6%', padding: '4px 2px' }} />
                      <Form.Control type="text" readOnly value={formData.allPos} style={{ fontSize: '11px', background: '#1b5e20', fontWeight: '700', border: '2px solid #1b5e20', borderRadius: '4px', textAlign: 'center', color: '#ffffff', width: '16.6%', padding: '4px 2px' }} />
                      <Form.Control type="text" readOnly value={formData.allPosHigh} style={{ fontSize: '11px', background: '#c8e6c9', fontWeight: '700', border: '1px solid #1b5e20', borderRadius: '4px', textAlign: 'center', color: '#1b5e20', width: '16.6%', padding: '4px 2px' }} />
                      <Form.Control type="text" readOnly value={formData.allNegLow} style={{ fontSize: '11px', background: '#ffcdd2', fontWeight: '700', border: '1px solid #7f0000', borderRadius: '4px', textAlign: 'center', color: '#7f0000', width: '16.6%', padding: '4px 2px' }} />
                      <Form.Control type="text" readOnly value={formData.allNeg} style={{ fontSize: '11px', background: '#7f0000', fontWeight: '700', border: '2px solid #7f0000', borderRadius: '4px', textAlign: 'center', color: '#ffffff', width: '16.6%', padding: '4px 2px' }} />
                      <Form.Control type="text" readOnly value={formData.allNegHigh} style={{ fontSize: '11px', background: '#ffcdd2', fontWeight: '700', border: '1px solid #7f0000', borderRadius: '4px', textAlign: 'center', color: '#7f0000', width: '16.6%', padding: '4px 2px' }} />
                    </div>
                  </Form.Group>
                </Col>
              </Row>

              {/* Row 4: Strike Positions, Ave Price, QTY, %, Rs, ND, (empty), (empty) */}
              <Row className="mb-3" style={{ margin: 0 }}>
                <Col xs={2} style={{ padding: '0 2px 0 0' }}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: '13px', fontWeight: '600', display: 'block', textAlign: 'center' }}>IXP</Form.Label>
                    <div style={{ display: 'flex', gap: '2px', marginBottom: '2px' }}>
                      <small style={{ width: '33%', textAlign: 'center', fontSize: '9px', color: '#6c757d' }}>L</small>
                      <small style={{ width: '33%', textAlign: 'center', fontSize: '9px', color: '#6c757d' }}>C</small>
                      <small style={{ width: '33%', textAlign: 'center', fontSize: '9px', color: '#6c757d' }}>H</small>
                    </div>
                    <div style={{ display: 'flex', gap: '2px' }}>
                      <Form.Control type="text" readOnly value={formData.ixpLow} style={{ fontSize: '11px', background: parseFloat(formData.ixpLow) >= 0 ? '#c8e6c9' : '#ffcdd2', fontWeight: '700', border: `1px solid ${parseFloat(formData.ixpLow) >= 0 ? '#1b5e20' : '#7f0000'}`, borderRadius: '4px', textAlign: 'center', color: parseFloat(formData.ixpLow) >= 0 ? '#1b5e20' : '#7f0000', width: '33%', padding: '4px 2px' }} />
                      <Form.Control type="text" readOnly value={formData.ixp} style={{ fontSize: '11px', background: parseFloat(formData.ixp) >= 0 ? '#1b5e20' : '#7f0000', fontWeight: '700', border: `2px solid ${parseFloat(formData.ixp) >= 0 ? '#1b5e20' : '#7f0000'}`, borderRadius: '4px', textAlign: 'center', color: '#ffffff', width: '33%', padding: '4px 2px' }} />
                      <Form.Control type="text" readOnly value={formData.ixpHigh} style={{ fontSize: '11px', background: parseFloat(formData.ixpHigh) >= 0 ? '#c8e6c9' : '#ffcdd2', fontWeight: '700', border: `1px solid ${parseFloat(formData.ixpHigh) >= 0 ? '#1b5e20' : '#7f0000'}`, borderRadius: '4px', textAlign: 'center', color: parseFloat(formData.ixpHigh) >= 0 ? '#1b5e20' : '#7f0000', width: '33%', padding: '4px 2px' }} />
                    </div>
                  </Form.Group>
                </Col>
                <Col xs={2} style={{ padding: '0 2px' }}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: '13px', fontWeight: '600', display: 'block', textAlign: 'center' }}>NIX15</Form.Label>
                    <div style={{ display: 'flex', gap: '2px', marginBottom: '2px' }}>
                      <small style={{ width: '33%', textAlign: 'center', fontSize: '9px', color: '#6c757d' }}>L</small>
                      <small style={{ width: '33%', textAlign: 'center', fontSize: '9px', color: '#6c757d' }}>C</small>
                      <small style={{ width: '33%', textAlign: 'center', fontSize: '9px', color: '#6c757d' }}>H</small>
                    </div>
                    <div style={{ display: 'flex', gap: '2px' }}>
                      <Form.Control type="text" readOnly value={formData.n15Low} style={{ fontSize: '11px', background: parseFloat(formData.n15Low) >= 0 ? '#c8e6c9' : '#ffcdd2', fontWeight: '700', border: `1px solid ${parseFloat(formData.n15Low) >= 0 ? '#1b5e20' : '#7f0000'}`, borderRadius: '4px', textAlign: 'center', color: parseFloat(formData.n15Low) >= 0 ? '#1b5e20' : '#7f0000', width: '33%', padding: '4px 2px' }} />
                      <Form.Control type="text" readOnly value={formData.n15} style={{ fontSize: '11px', background: parseFloat(formData.n15) >= 0 ? '#1b5e20' : '#7f0000', fontWeight: '700', border: `2px solid ${parseFloat(formData.n15) >= 0 ? '#1b5e20' : '#7f0000'}`, borderRadius: '4px', textAlign: 'center', color: '#ffffff', width: '33%', padding: '4px 2px' }} />
                      <Form.Control type="text" readOnly value={formData.n15High} style={{ fontSize: '11px', background: parseFloat(formData.n15High) >= 0 ? '#c8e6c9' : '#ffcdd2', fontWeight: '700', border: `1px solid ${parseFloat(formData.n15High) >= 0 ? '#1b5e20' : '#7f0000'}`, borderRadius: '4px', textAlign: 'center', color: parseFloat(formData.n15High) >= 0 ? '#1b5e20' : '#7f0000', width: '33%', padding: '4px 2px' }} />
                    </div>
                  </Form.Group>
                </Col>
                <Col xs={2} style={{ padding: '0 2px' }}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: '13px', fontWeight: '600', display: 'block', textAlign: 'center' }}>N50 P</Form.Label>
                    <div style={{ display: 'flex', gap: '2px', marginBottom: '2px' }}>
                      <small style={{ width: '33%', textAlign: 'center', fontSize: '9px', color: '#6c757d' }}>L</small>
                      <small style={{ width: '33%', textAlign: 'center', fontSize: '9px', color: '#6c757d' }}>C</small>
                      <small style={{ width: '33%', textAlign: 'center', fontSize: '9px', color: '#6c757d' }}>H</small>
                    </div>
                    <div style={{ display: 'flex', gap: '2px' }}>
                      <Form.Control type="text" readOnly value={formData.n50PLow} style={{ fontSize: '11px', background: parseFloat(formData.n50PLow) >= 0 ? '#c8e6c9' : '#ffcdd2', fontWeight: '700', border: `1px solid ${parseFloat(formData.n50PLow) >= 0 ? '#1b5e20' : '#7f0000'}`, borderRadius: '4px', textAlign: 'center', color: parseFloat(formData.n50PLow) >= 0 ? '#1b5e20' : '#7f0000', width: '33%', padding: '4px 2px' }} />
                      <Form.Control type="text" readOnly value={formData.n50P} style={{ fontSize: '11px', background: parseFloat(formData.n50P) >= 0 ? '#1b5e20' : '#7f0000', fontWeight: '700', border: `2px solid ${parseFloat(formData.n50P) >= 0 ? '#1b5e20' : '#7f0000'}`, borderRadius: '4px', textAlign: 'center', color: '#ffffff', width: '33%', padding: '4px 2px' }} />
                      <Form.Control type="text" readOnly value={formData.n50PHigh} style={{ fontSize: '11px', background: parseFloat(formData.n50PHigh) >= 0 ? '#c8e6c9' : '#ffcdd2', fontWeight: '700', border: `1px solid ${parseFloat(formData.n50PHigh) >= 0 ? '#1b5e20' : '#7f0000'}`, borderRadius: '4px', textAlign: 'center', color: parseFloat(formData.n50PHigh) >= 0 ? '#1b5e20' : '#7f0000', width: '33%', padding: '4px 2px' }} />
                    </div>
                  </Form.Group>
                </Col>
                <Col xs={6} style={{ padding: '0 0 0 2px' }}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: '11px', fontWeight: '600', display: 'block', textAlign: 'center' }}>No Of Stock (N50)</Form.Label>
                    <div style={{ display: 'flex', gap: '2px', marginBottom: '2px' }}>
                      <small style={{ width: '16.6%', textAlign: 'center', fontSize: '9px', color: '#6c757d' }}>L</small>
                      <small style={{ width: '16.6%', textAlign: 'center', fontSize: '9px', color: '#6c757d' }}>C</small>
                      <small style={{ width: '16.6%', textAlign: 'center', fontSize: '9px', color: '#6c757d' }}>H</small>
                      <small style={{ width: '16.6%', textAlign: 'center', fontSize: '9px', color: '#6c757d' }}>L</small>
                      <small style={{ width: '16.6%', textAlign: 'center', fontSize: '9px', color: '#6c757d' }}>C</small>
                      <small style={{ width: '16.6%', textAlign: 'center', fontSize: '9px', color: '#6c757d' }}>H</small>
                    </div>
                    <div style={{ display: 'flex', gap: '2px' }}>
                      <Form.Control type="text" readOnly value={formData.n50PosLow} style={{ fontSize: '11px', background: '#c8e6c9', fontWeight: '700', border: '1px solid #1b5e20', borderRadius: '4px', textAlign: 'center', color: '#1b5e20', width: '16.6%', padding: '4px 2px' }} />
                      <Form.Control type="text" readOnly value={formData.n50Pos} style={{ fontSize: '11px', background: '#1b5e20', fontWeight: '700', border: '2px solid #1b5e20', borderRadius: '4px', textAlign: 'center', color: '#ffffff', width: '16.6%', padding: '4px 2px' }} />
                      <Form.Control type="text" readOnly value={formData.n50PosHigh} style={{ fontSize: '11px', background: '#c8e6c9', fontWeight: '700', border: '1px solid #1b5e20', borderRadius: '4px', textAlign: 'center', color: '#1b5e20', width: '16.6%', padding: '4px 2px' }} />
                      <Form.Control type="text" readOnly value={formData.n50NegLow} style={{ fontSize: '11px', background: '#ffcdd2', fontWeight: '700', border: '1px solid #7f0000', borderRadius: '4px', textAlign: 'center', color: '#7f0000', width: '16.6%', padding: '4px 2px' }} />
                      <Form.Control type="text" readOnly value={formData.n50Neg} style={{ fontSize: '11px', background: '#7f0000', fontWeight: '700', border: '2px solid #7f0000', borderRadius: '4px', textAlign: 'center', color: '#ffffff', width: '16.6%', padding: '4px 2px' }} />
                      <Form.Control type="text" readOnly value={formData.n50NegHigh} style={{ fontSize: '11px', background: '#ffcdd2', fontWeight: '700', border: '1px solid #7f0000', borderRadius: '4px', textAlign: 'center', color: '#7f0000', width: '16.6%', padding: '4px 2px' }} />
                    </div>
                  </Form.Group>
                </Col>
              </Row>

              {/* Row 5: IXL, All%L, N50%L, N10%L, N%L, NFUT */}
              <Row className="mb-3" style={{ margin: 0 }}>
                <Col xs={2} style={{ padding: '0 2px 0 0' }}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: '13px', fontWeight: '600', display: 'block', textAlign: 'center' }}>IXL</Form.Label>
                    <Form.Control
                      type="text"
                      name="ixl"
                      value={formData.ixl}
                      readOnly
                      style={{
                        fontSize: '14px',
                        background: parseFloat(formData.ixl) >= 50 ? '#e8f5e9' : '#ffebee',
                        fontWeight: '700',
                        border: `2px solid ${parseFloat(formData.ixl) >= 50 ? '#4caf50' : '#f44336'}`,
                        borderRadius: '4px',
                        textAlign: 'center',
                        color: parseFloat(formData.ixl) >= 50 ? '#2e7d32' : '#c62828'
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col xs={2} style={{ padding: '0 2px' }}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: '13px', fontWeight: '600', display: 'block', textAlign: 'center' }}>All%L</Form.Label>
                    <Form.Control
                      type="text"
                      name="allPercL"
                      value={formData.allPercL}
                      readOnly
                      style={{
                        fontSize: '14px',
                        background: parseFloat(formData.allPercL) >= 50 ? '#e8f5e9' : '#ffebee',
                        fontWeight: '700',
                        border: `2px solid ${parseFloat(formData.allPercL) >= 50 ? '#4caf50' : '#f44336'}`,
                        borderRadius: '4px',
                        textAlign: 'center',
                        color: parseFloat(formData.allPercL) >= 50 ? '#2e7d32' : '#c62828'
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col xs={2} style={{ padding: '0 2px' }}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: '13px', fontWeight: '600', display: 'block', textAlign: 'center' }}>N50%L</Form.Label>
                    <Form.Control
                      type="text"
                      name="n50PercL"
                      value={formData.n50PercL}
                      readOnly
                      style={{
                        fontSize: '14px',
                        background: parseFloat(formData.n50PercL) >= 50 ? '#e8f5e9' : '#ffebee',
                        fontWeight: '700',
                        border: `2px solid ${parseFloat(formData.n50PercL) >= 50 ? '#4caf50' : '#f44336'}`,
                        borderRadius: '4px',
                        textAlign: 'center',
                        color: parseFloat(formData.n50PercL) >= 50 ? '#2e7d32' : '#c62828'
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col xs={2} style={{ padding: '0 2px' }}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: '13px', fontWeight: '600', display: 'block', textAlign: 'center' }}>N10%L</Form.Label>
                    <Form.Control
                      type="text"
                      name="n10PercL"
                      value={formData.n10PercL}
                      readOnly
                      style={{
                        fontSize: '14px',
                        background: parseFloat(formData.n10PercL) >= 50 ? '#e8f5e9' : '#ffebee',
                        fontWeight: '700',
                        border: `2px solid ${parseFloat(formData.n10PercL) >= 50 ? '#4caf50' : '#f44336'}`,
                        borderRadius: '4px',
                        textAlign: 'center',
                        color: parseFloat(formData.n10PercL) >= 50 ? '#2e7d32' : '#c62828'
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col xs={2} style={{ padding: '0 0 0 2px' }}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: '13px', fontWeight: '600', display: 'block', textAlign: 'center' }}>N%L</Form.Label>
                    <Form.Control
                      type="text"
                      name="nPercL"
                      value={formData.nPercL}
                      readOnly
                      style={{
                        fontSize: '14px',
                        background: parseFloat(formData.nPercL) >= 50 ? '#e8f5e9' : '#ffebee',
                        fontWeight: '700',
                        border: `2px solid ${parseFloat(formData.nPercL) >= 50 ? '#4caf50' : '#f44336'}`,
                        borderRadius: '4px',
                        textAlign: 'center',
                        color: parseFloat(formData.nPercL) >= 50 ? '#2e7d32' : '#c62828'
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col xs={2} style={{ padding: '0 2px' }}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: '13px', fontWeight: '600', display: 'block', textAlign: 'center' }}>NFUT</Form.Label>
                    <Form.Control
                      type="text"
                      name="nfut"
                      value={formData.nfut}
                      readOnly
                      style={{
                        fontSize: '13px',
                        background: formData.nfut === '' ? '#f8f9fa' : (parseFloat(formData.nfut) >= 1 ? '#e8f5e9' : '#ffebee'),
                        fontWeight: '700',
                        border: formData.nfut === '' ? '1px solid #dee2e6' : `2px solid ${parseFloat(formData.nfut) >= 1 ? '#4caf50' : '#f44336'}`,
                        borderRadius: '4px',
                        textAlign: 'center',
                        color: formData.nfut === '' ? '#6c757d' : (parseFloat(formData.nfut) >= 1 ? '#2e7d32' : '#c62828')
                      }}
                    />
                  </Form.Group>
                </Col>
                {/* <Col>
                  <Form.Group>
                    <Form.Label style={{ fontSize: '13px', fontWeight: '600', display: 'block', textAlign: 'center' }}>&nbsp;</Form.Label>
                    <Form.Control type="text" disabled style={{ fontSize: '13px', background: '#f8f9fa' }} />
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group>
                    <Form.Label style={{ fontSize: '13px', fontWeight: '600', display: 'block', textAlign: 'center' }}>&nbsp;</Form.Label>
                    <Form.Control type="text" disabled style={{ fontSize: '13px', background: '#f8f9fa' }} />
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group>
                    <Form.Label style={{ fontSize: '13px', fontWeight: '600', display: 'block', textAlign: 'center' }}>&nbsp;</Form.Label>
                    <Form.Control type="text" disabled style={{ fontSize: '13px', background: '#f8f9fa' }} />
                  </Form.Group>
                </Col> */}
              </Row>

              {/* Row 6: BN, IT, T3, T10T */}
              <Row className="mb-3" style={{ margin: 0 }}>
                <Col xs={3} style={{ padding: '0 2px 0 0' }}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: '13px', fontWeight: '600', display: 'block', textAlign: 'center' }}>BNL</Form.Label>
                    <Form.Control
                      type="text"
                      name="bnl"
                      value={formData.bnl}
                      readOnly
                      style={{
                        fontSize: '14px',
                        background: parseFloat(formData.bnl) >= 50 ? '#e8f5e9' : '#ffebee',
                        fontWeight: '700',
                        border: `2px solid ${parseFloat(formData.bnl) >= 50 ? '#4caf50' : '#f44336'}`,
                        borderRadius: '4px',
                        textAlign: 'center',
                        color: parseFloat(formData.bnl) >= 50 ? '#2e7d32' : '#c62828'
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col xs={3} style={{ padding: '0 2px' }}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: '13px', fontWeight: '600', display: 'block', textAlign: 'center' }}>ITL</Form.Label>
                    <Form.Control
                      type="text"
                      name="itl"
                      value={formData.itl}
                      readOnly
                      style={{
                        fontSize: '14px',
                        background: parseFloat(formData.itl) >= 50 ? '#e8f5e9' : '#ffebee',
                        fontWeight: '700',
                        border: `2px solid ${parseFloat(formData.itl) >= 50 ? '#4caf50' : '#f44336'}`,
                        borderRadius: '4px',
                        textAlign: 'center',
                        color: parseFloat(formData.itl) >= 50 ? '#2e7d32' : '#c62828'
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col xs={3} style={{ padding: '0 2px' }}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: '13px', fontWeight: '600', display: 'block', textAlign: 'center' }}>T3L</Form.Label>
                    <Form.Control
                      type="text"
                      name="t3l"
                      value={formData.t3l}
                      readOnly
                      style={{
                        fontSize: '14px',
                        background: parseFloat(formData.t3l) >= 50 ? '#e8f5e9' : '#ffebee',
                        fontWeight: '700',
                        border: `2px solid ${parseFloat(formData.t3l) >= 50 ? '#4caf50' : '#f44336'}`,
                        borderRadius: '4px',
                        textAlign: 'center',
                        color: parseFloat(formData.t3l) >= 50 ? '#2e7d32' : '#c62828'
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col xs={3} style={{ padding: '0 0 0 2px' }}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: '13px', fontWeight: '600', display: 'block', textAlign: 'center' }}>T10P</Form.Label>
                    <Form.Control
                      type="text"
                      name="t10p"
                      value={formData.t10p}
                      readOnly
                      style={{
                        fontSize: '14px',
                        background: parseFloat(formData.t10p) >= 0 ? '#e8f5e9' : '#ffebee',
                        fontWeight: '700',
                        border: `2px solid ${parseFloat(formData.t10p) >= 0 ? '#4caf50' : '#f44336'}`,
                        borderRadius: '4px',
                        textAlign: 'center',
                        color: parseFloat(formData.t10p) >= 0 ? '#2e7d32' : '#c62828'
                      }}
                    />
                  </Form.Group>
                </Col>
              </Row>




            </Card.Body>
          </Card>
      </Container>
    </MainLayout>
  );
};

export default LivePage;
